/**
 * TanStack Query hooks for the signed-in user's in-app notifications (RP-34).
 *
 * The bell in the status bar polls once a minute, and the inbox page every 20 seconds while
 * it is open, so a new notice arrives without a reload; there is no push channel yet.
 *
 * Dismissing a notice marks it read: it leaves the bell and the inbox's New view, and stays
 * in All as history.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { send } from "@/hooks/use-resource-plans";
import { notificationKeys } from "@/lib/query";
import type { NotificationListResponse } from "@/schemas/notifications";

/** How many unread notices the bell's panel shows; the inbox has the rest. */
export const BELL_LIMIT = 8;

export type InboxView = "new" | "all";

export function useUnreadNotifications() {
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: () =>
      send<NotificationListResponse>(`/api/notifications?unread=true&limit=${BELL_LIMIT}`, "GET"),
    refetchInterval: 60_000,
  });
}

function firstInboxPage(): string | null {
  return null;
}

export function useNotificationInbox(view: InboxView) {
  return useInfiniteQuery({
    queryKey: notificationKeys.inbox(view),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: "25" });
      if (view === "new") params.set("unread", "true");
      if (pageParam !== null) params.set("cursor", pageParam);
      return send<NotificationListResponse>(`/api/notifications?${params}`, "GET");
    },
    initialPageParam: firstInboxPage(),
    getNextPageParam: (last: NotificationListResponse) => (last.hasNext ? last.nextCursor : null),
    refetchInterval: 20_000,
  });
}

/** Mark one notice read — on opening it, or by dismissing it. */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      send<{ ok: true }>(`/api/notifications/${notificationId}/read`, "POST"),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
    onError: (err: Error) => toast.error(err.message || "Failed to dismiss the notification"),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => send<{ updated: number }>("/api/notifications/read-all", "POST"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      toast.success("All notifications dismissed");
    },
    onError: (err: Error) => toast.error(err.message || "Failed to dismiss notifications"),
  });
}
